import * as THREE from 'three';
import {
    axisEdges,
    getWallNormalAxis,
    getWallSurfaceBox,
    L_SHAPE_ARM_WALL,
    L_SHAPE_LEG_WALL,
} from 'components/customer/RoomPlanner/helpers/wallProximity';
import type {PlanAxis} from 'components/customer/RoomPlanner/helpers/calculations';
import {axisVector} from 'components/customer/RoomPlanner/helpers/axes';
import {isCornerLShapedCabinet} from 'components/customer/RoomPlanner/helpers/shapeProducts';

type SnapSide = 'min' | 'max';

// Which cabinet face was flush to the wall before rotation: the closest one.
const getFlushSide = (
    cabinetBox: THREE.Box3,
    wallBox: THREE.Box3,
    axis: PlanAxis
): SnapSide => {
    const {min: cMin, max: cMax} = axisEdges(cabinetBox, axis);
    const {min: wMin, max: wMax} = axisEdges(wallBox, axis);

    return Math.min(Math.abs(cMin - wMin), Math.abs(cMin - wMax)) <=
        Math.min(Math.abs(cMax - wMin), Math.abs(cMax - wMax))
        ? 'min'
        : 'max';
};

const CORNER_FLUSH_THRESHOLD = 50;

const getCornerFlushDelta = (
    postBox: THREE.Box3,
    parallelAxis: PlanAxis,
    wall: THREE.Mesh,
    threshold: number
): number => {
    const siblings = wall.parent?.children ?? [];
    const {min: cMin, max: cMax} = axisEdges(postBox, parallelAxis);
    let best = 0;
    let bestDist = threshold;
    siblings.forEach((sib) => {
        const mesh = sib as THREE.Mesh;
        if (
            mesh === wall ||
            !mesh.isMesh ||
            !mesh.geometry ||
            !(mesh.name.includes('wall') || mesh.name.includes('Wall'))
        ) {
            return;
        }
        const sBox = getWallSurfaceBox(mesh);
        if (sBox.isEmpty()) return;
        if (getWallNormalAxis(sBox) !== parallelAxis) return;
        const {min: wMin, max: wMax} = axisEdges(sBox, parallelAxis);
        const dMinFace = wMax - cMin;
        const dMaxFace = wMin - cMax;
        if (Math.abs(dMinFace) < bestDist) {
            bestDist = Math.abs(dMinFace);
            best = dMinFace;
        }
        if (Math.abs(dMaxFace) < bestDist) {
            bestDist = Math.abs(dMaxFace);
            best = dMaxFace;
        }
    });
    return best;
};

const faceCoord = (box: THREE.Box3, side: SnapSide, axis: PlanAxis): number => {
    const {min, max} = axisEdges(box, axis);

    return side === 'min' ? min : max;
};

// Restores the pre-rotation gap between the wall surface and the cabinet face
// that was facing it, by translating on the wall-normal axis. Pure geometry, so
// it behaves the same in both room types for base and upper products.
//
// `preRotationBox` is the world AABB before rotation. Returns null when the
// cabinet is not near the wall.
export const reflushAfterRotation = (
    group: THREE.Group,
    wall: THREE.Mesh | null,
    preRotationBox: THREE.Box3
): THREE.Vector3 | null => {
    const isUpper = Boolean(group.userData.isUpperProduct);

    const isCornerCabinet = isCornerLShapedCabinet(group);

    if (!wall) return null;

    const wallBox = getWallSurfaceBox(wall);
    if (wallBox.isEmpty()) return null;

    const axis = getWallNormalAxis(wallBox);
    const flushSide = getFlushSide(preRotationBox, wallBox, axis);

    // Wall surface the pre-rotation face was closest to, and the signed gap to
    // it, so which side of the wall AABB the cabinet sat on is preserved.
    const {min: wallMin, max: wallMax} = axisEdges(wallBox, axis);
    const preFaceCoord = faceCoord(preRotationBox, flushSide, axis);

    const wallSurface =
        Math.abs(preFaceCoord - wallMin) < Math.abs(preFaceCoord - wallMax)
            ? wallMin
            : wallMax;
    let signedGap = preFaceCoord - wallSurface;
    const measuredGap = Math.abs(signedGap);

    // baseProductClamping has no flush case for the inner L walls, so base
    // cabinets land there with a loose drag-time gap. Zero it to match uppers.
    const isInnerLShapeWall =
        wall.name === L_SHAPE_LEG_WALL || wall.name === L_SHAPE_ARM_WALL;
    if (isInnerLShapeWall && !isUpper) {
        signedGap = 0;
    }

    // Uppers must sit flush, never preserve a gap.
    if (isUpper) {
        signedGap = 0;
    }

    // currentWall comes from the drag raycaster, so it can point at a wall the
    // cabinet is nowhere near. Drag-time wallOffset is ~26, so treat anything
    // within 60 as snapped.
    const FLUSH_THRESHOLD = 60;
    if (measuredGap > FLUSH_THRESHOLD) return null;

    // setFromObject is safe here: cabinet groups carry no dimension arrows.
    group.updateMatrixWorld();
    const postBox = new THREE.Box3().setFromObject(group);

    const delta = wallSurface + signedGap - faceCoord(postBox, flushSide, axis);
    const newPos = group.position.clone().add(axisVector(axis, delta));
    const parallelAxis: PlanAxis = axis === 'x' ? 'z' : 'x';

    if (isCornerCabinet) {
        const cornerDelta = getCornerFlushDelta(
            postBox,
            parallelAxis,
            wall,
            CORNER_FLUSH_THRESHOLD
        );
        if (cornerDelta !== 0) {
            return newPos.add(axisVector(parallelAxis, cornerDelta));
        }
    }

    // Keep the rotated AABB within the wall span. A cabinet now wider than the
    // segment goes to the nearest in-bounds edge, not across the corner.
    const post = axisEdges(postBox, parallelAxis);
    const wallPara = axisEdges(wallBox, parallelAxis);

    let paraDelta = 0;
    if (post.min < wallPara.min) paraDelta = wallPara.min - post.min;
    else if (post.max > wallPara.max) paraDelta = wallPara.max - post.max;

    return newPos.add(axisVector(parallelAxis, paraDelta));
};
